import { useCallback, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { makeStyles, shorthands, mergeClasses, Tooltip } from "@fluentui/react-components";
import { ListIcon, HouseIcon, InfoIcon, HandshakeIcon, ShieldCheckIcon, ChatTextIcon, UserIcon, XIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { isInStandaloneMode } from "../../services/pwaInstall";
import { useAppSelector, selectUserDisplayName, selectAuthEnabled, selectAppFeedbackEnabled } from "../../store";

// Import theme tokens
import { tokens, cssVar } from "../../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// Create local references for frequently used tokens
// =============================================================================

const t = {
    // Navigation
    navWidthCollapsed: tokens.components.navWidthCollapsed,
    navWidthExpanded: tokens.components.navWidthExpanded,
    navBackground: tokens.components.navBackground,
    navPaddingHoriztonal: tokens.components.navPaddingHorizontal,
    navPaddingVertical: tokens.components.navPaddingVertical,

    // Spacing
    spacingS: tokens.primitives.spacingSm,
    spacingMd: tokens.primitives.spacingMd,
    spacingLg: tokens.primitives.spacingLg,
    spacingXl: tokens.primitives.spacingXl,
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],

    // Typography
    fontSizeBase: tokens.primitives["fontSizeBase"],
    fontSizeMd: tokens.primitives["fontSizeMd"],
    fontSizeLg: cssVar("font-size-lg"),
    fontWeightRegular: tokens.primitives.fontWeightRegular,
    fontWeightMedium: tokens.primitives.fontWeightMedium,
    fontWeightSemibold: tokens.primitives.fontWeightSemibold,
    fontFamilySecondary: tokens.primitives.fontFamilySecondary,
    lineHeightMd: cssVar("line-height-md"),

    // Colors
    brandPrimary: tokens.legacy.colorBrandPrimary,
    themeLight: tokens.palette["theme-Light"],
    textMutedTransparent: tokens.legacy.colorTextMutedTransparent,
    borderSubtle: tokens.legacy.colorBorderSubtle,

    // Radius
    radiusMedium: tokens.primitives["stroke-Radius-Medium"],

    // Shadows & Transitions
    shadowNavigation: tokens.legacy.shadowNavigation,
    transitionDefault: tokens.legacy.transitionDefault,

    // Button - Subtle Primary Inverse (for nav items on dark background)
    btnInverseBg: tokens.buttons.buttonSubtlePrimaryInverseBackground,
    btnInverseFg: tokens.buttons.buttonSubtlePrimaryInverseForeground,
    btnInverseStroke: tokens.buttons.buttonSubtlePrimaryInverseStroke,
    btnInverseBgHover: tokens.buttons.buttonSubtlePrimaryInverseBackgroundHover,
    btnInverseFgHover: tokens.buttons.buttonSubtlePrimaryInverseForegroundHover,
    btnInverseStrokeHover: tokens.buttons.buttonSubtlePrimaryInverseStrokeHover,
    btnInverseBgPressed: tokens.buttons.buttonSubtlePrimaryInverseBackgroundPressed,
    btnInverseFgPressed: tokens.buttons.buttonSubtlePrimaryInverseForegroundPressed,
    btnInverseStrokePressed: tokens.buttons.buttonSubtlePrimaryInverseStrokePressed,
    btnInverseBgSelected: tokens.buttons.buttonSubtlePrimaryInverseBackgroundSelected,
    btnInverseFgSelected: tokens.buttons.buttonSubtlePrimaryInverseForegroundSelected,
    btnInverseStrokeSelected: tokens.buttons.buttonSubtlePrimaryInverseStrokeSelected
};

// Helper for setting all border colors at once
const allBorderColors = (color: string) => ({
    borderTopColor: color,
    borderRightColor: color,
    borderBottomColor: color,
    borderLeftColor: color
});

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    mobileOverlay: {
        display: "none"
    },

    mobileOverlayVisible: {},

    navigationBar: {
        display: "flex",
        flexDirection: "column",
        width: t.navWidthCollapsed,
        minWidth: t.navWidthCollapsed,
        minHeight: "100%",
        backgroundColor: t.navBackground,
        transitionProperty: "width, transform",
        transitionDuration: t.transitionDefault,
        transitionTimingFunction: "ease",

        "& a": {
            textDecoration: "none",
            color: t.brandPrimary
        },

        "@media screen and (max-width: 480px)": {
            width: "0",
            minWidth: "0",
            overflow: "hidden"
        }
    },

    expanded: {
        width: t.navWidthExpanded,
        boxShadow: t.shadowNavigation,

        "@media screen and (max-width: 480px)": {
            width: t.navWidthExpanded,
            minWidth: t.navWidthExpanded
        }
    },

    mobileHeader: {
        display: "none"
    },

    mobileTitle: {
        color: t.themeLight,
        fontSize: t.fontSizeLg,
        fontWeight: t.fontWeightSemibold as unknown as number,
        margin: 0
    },

    closeButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "44px",
        height: "44px",
        padding: 0,
        border: "none",
        borderRadius: t.radiusMedium,
        backgroundColor: "transparent",
        color: t.themeLight,
        cursor: "pointer",
        transitionProperty: "background-color",
        transitionDuration: t.transitionDefault,

        "&:hover": {
            backgroundColor: t.btnInverseBgHover
        },

        "&:active": {
            backgroundColor: t.btnInverseBgPressed
        }
    },

    menuButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        minWidth: "40px",
        minHeight: "40px",
        padding: "4px 10px",
        margin: "8px 8px 0",
        rowGap: t.spacingHorizontalM,
        columnGap: t.spacingHorizontalM,
        borderRadius: t.radiusMedium,
        border: "1px solid transparent",
        ...allBorderColors(t.btnInverseStroke),
        backgroundColor: t.btnInverseBg,
        color: t.btnInverseFg,
        cursor: "pointer",
        transitionProperty: "background-color, border-color, color",
        transitionDuration: t.transitionDefault,
        transitionTimingFunction: "ease",

        "&:hover": {
            backgroundColor: t.btnInverseBgHover,
            ...allBorderColors(t.btnInverseStrokeHover),
            color: t.btnInverseFgHover
        },

        "&:active": {
            backgroundColor: t.btnInverseBgPressed,
            ...allBorderColors(t.btnInverseStrokePressed),
            color: t.btnInverseFgPressed
        },

        "& svg": {
            flexShrink: 0,
            minWidth: "28px",
            minHeight: "28px"
        },

        "@media screen and (max-width: 480px)": {
            display: "flex",
            margin: `${t.spacingMd} ${t.spacingMd} 0`
        }
    },

    menuButtonExpanded: {
        flexDirection: "row",
        justifyContent: "flex-start"
    },

    menuButtonText: {
        fontWeight: t.fontWeightMedium as unknown as number,
        color: t.themeLight,
        fontSize: t.fontSizeMd,
        opacity: 0,
        visibility: "hidden" as const,
        transitionProperty: "opacity, visibility",
        transitionDuration: t.transitionDefault,
        whiteSpace: "nowrap"
    },

    menuButtonTextVisible: {
        opacity: 1,
        visibility: "visible" as const
    },

    navigationMenus: {
        flexGrow: 1,
        listStyleType: "none",
        padding: "0",
        margin: "80px 0 0 0",
        display: "flex",
        flexDirection: "column",
        rowGap: t.spacingVerticalS,
        columnGap: t.spacingVerticalS,
        overflowX: "hidden",
        overflowY: "auto",

        "@media screen and (max-width: 480px)": {
            margin: "80px 0 0 0",
            rowGap: t.spacingVerticalXs
        }
    },

    navItem: {
        display: "flex",
        alignItems: "center",
        borderRadius: t.radiusMedium,
        margin: "0 8px",
        padding: "4px 10px",
        minHeight: "40px",
        cursor: "pointer",
        transitionProperty: "background-color, padding-left",
        transitionDuration: t.transitionDefault,
        transitionTimingFunction: "ease",
        justifyContent: "center",

        backgroundColor: t.btnInverseBg,
        ...allBorderColors(t.btnInverseStroke),
        color: t.btnInverseFg,

        "& a": {
            display: "flex",
            alignItems: "center",
            width: "100%",
            color: "inherit"
        },

        "&:hover": {
            backgroundColor: t.btnInverseBgHover,
            ...allBorderColors(t.btnInverseStrokeHover),
            color: t.btnInverseFgHover
        },

        "&:active": {
            backgroundColor: t.btnInverseBgPressed,
            ...allBorderColors(t.btnInverseStrokePressed),
            color: t.btnInverseFgPressed
        },

        "@media screen and (max-width: 480px)": {
            justifyContent: "flex-start",
            minHeight: "48px",
            padding: `${t.spacingVerticalM} ${t.spacingMd}`
        }
    },

    navItemActive: {
        backgroundColor: t.btnInverseBgSelected,
        color: t.btnInverseFgSelected,

        "&:hover": {
            backgroundColor: t.btnInverseBgHover,
            ...allBorderColors(t.btnInverseStrokeHover),
            color: t.btnInverseFgHover
        }
    },

    navItemIcon: {
        minWidth: "24px",
        minHeight: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
    },

    navItemText: {
        fontSize: t.fontSizeMd,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightMd,
        marginLeft: t.spacingHorizontalM,
        opacity: 0,
        visibility: "hidden" as const,
        whiteSpace: "nowrap",
        transitionProperty: "opacity, visibility",
        transitionDuration: t.transitionDefault,

        "@media screen and (max-width: 480px)": {
            opacity: 1,
            visibility: "visible" as const
        }
    },

    navItemTextVisible: {
        opacity: 1,
        visibility: "visible" as const
    },

    userProfile: {
        marginTop: "auto",
        paddingTop: t.spacingMd,
        paddingBottom: t.spacingXl,

        "@media screen and (max-width: 480px)": {
            paddingBottom: t.spacingLg
        }
    },

    userProfileItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 8px",
        padding: "4px 10px",
        minHeight: "40px",
        borderRadius: t.radiusMedium,
        border: "1px solid transparent",
        ...allBorderColors(t.btnInverseStroke),
        backgroundColor: t.btnInverseBg,
        color: t.btnInverseFg,
        cursor: "pointer",
        transitionProperty: "background-color, border-color, color",
        transitionDuration: t.transitionDefault,
        transitionTimingFunction: "ease",

        "& a": {
            display: "flex",
            alignItems: "center",
            width: "100%",
            color: "inherit"
        },

        "&:hover": {
            backgroundColor: t.btnInverseBgHover,
            ...allBorderColors(t.btnInverseStrokeHover),
            color: t.btnInverseFgHover
        },

        "&:active": {
            backgroundColor: t.btnInverseBgPressed,
            ...allBorderColors(t.btnInverseStrokePressed),
            color: t.btnInverseFgPressed
        },

        "@media screen and (max-width: 480px)": {
            justifyContent: "flex-start",
            minHeight: "48px",
            padding: `${t.spacingVerticalM} ${t.spacingMd}`
        }
    },

    userProfileItemActive: {
        backgroundColor: t.btnInverseBgSelected,
        ...allBorderColors(t.btnInverseStrokeSelected),
        color: t.btnInverseFgSelected,

        "&:hover": {
            backgroundColor: t.btnInverseBgHover,
            ...allBorderColors(t.btnInverseStrokeHover),
            color: t.btnInverseFgHover
        }
    },

    userProfileText: {
        fontSize: t.fontSizeMd,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightMd,
        marginLeft: t.spacingHorizontalM,
        opacity: 0,
        visibility: "hidden" as const,
        whiteSpace: "nowrap",
        transitionProperty: "opacity, visibility",
        transitionDuration: t.transitionDefault,

        "@media screen and (max-width: 480px)": {
            opacity: 1,
            visibility: "visible" as const
        }
    },

    userProfileTextVisible: {
        opacity: 1,
        visibility: "visible" as const
    },

    footer: {
        visibility: "hidden" as const,
        paddingBottom: t.spacingLg,
        ...shorthands.margin("0", t.spacingMd),

        "& p": {
            color: t.textMutedTransparent,
            fontSize: t.fontSizeBase,
            fontWeight: t.fontWeightRegular as unknown as number,
            fontFamily: t.fontFamilySecondary
        },

        "& img": {
            width: "225px",
            filter: "brightness(0) saturate(100%) invert(30%) sepia(10%) saturate(2093%) hue-rotate(178deg) brightness(95%) contrast(86%)"
        },

        "@media screen and (max-width: 480px)": {
            visibility: "visible" as const
        }
    },

    footerVisible: {
        visibility: "visible" as const
    },

    versionText: {
        display: "block",
        textAlign: "center" as const,
        color: t.textMutedTransparent,
        fontSize: t.fontSizeBase,
        cursor: "default",
        paddingBottom: t.spacingS
    }
});

// =============================================================================
// TYPES
// =============================================================================

interface NavigationItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    path?: string;
    href?: string;
    isExternal?: boolean;
}

// Update the interface - remove userName prop
interface NavigationBarProps {
    isMenuOpen: boolean;
    toggleNav: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

const NavigationBar = ({ isMenuOpen, toggleNav }: NavigationBarProps) => {
    const styles = useStyles();
    const location = useLocation();
    const userName = useAppSelector(selectUserDisplayName) ?? "User";
    const authEnabled = useAppSelector(selectAuthEnabled);
    const appFeedbackEnabled = useAppSelector(selectAppFeedbackEnabled);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 480 && isMenuOpen) {
                // Menu stays open, but we could add logic here if needed
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isMenuOpen]);

    useEffect(() => {
        if (window.innerWidth <= 480 && isMenuOpen) {
            toggleNav();
        }
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const navigationItems: NavigationItem[] = useMemo(() => {
        const items: NavigationItem[] = [
            { id: "home", label: "Home", icon: <HouseIcon size={28} />, path: "/" },
            brand.about.href
                ? { id: "about", label: "About", icon: <InfoIcon size={28} />, href: brand.about.href, isExternal: true }
                : { id: "about", label: "About", icon: <InfoIcon size={28} />, path: "/about" },
            { id: "terms", label: "Terms of Use", icon: <HandshakeIcon size={28} />, path: "/terms_of_use" },
            { id: "privacy", label: "Privacy Policy", icon: <ShieldCheckIcon size={28} />, path: "/privacy_policy" }
        ];

        // Only show feedback if enabled
        if (appFeedbackEnabled) {
            items.push({ id: "feedback", label: "Share Feedback", icon: <ChatTextIcon size={28} />, path: "/feedback" });
        }

        // Show "Install App" only on mobile and not already installed
        const mobile = typeof window !== "undefined" && window.innerWidth <= 480;
        if (brand.features.installApp && mobile && !isInStandaloneMode()) {
            items.push({ id: "install", label: "Install App", icon: <DownloadSimpleIcon size={28} />, path: "/install" });
        }

        return items;
    }, [appFeedbackEnabled]);

    const isActive = useCallback(
        (path?: string) => {
            if (!path) return false;
            return location.pathname === path;
        },
        [location.pathname]
    );

    const isMobile = typeof window !== "undefined" && window.innerWidth <= 480;

    const renderNavItemContent = (item: NavigationItem) => (
        <>
            <span className={styles.navItemIcon} aria-hidden="true">
                {item.icon}
            </span>
            <span className={mergeClasses(styles.navItemText, isMenuOpen && styles.navItemTextVisible)}>{item.label}</span>
        </>
    );

    const renderNavItem = (item: NavigationItem) => {
        const itemClasses = mergeClasses(styles.navItem, isActive(item.path) && styles.navItemActive);
        const isCurrentPage = isActive(item.path);

        if (!isMenuOpen && !isMobile) {
            // Collapsed nav: wrap Link in Tooltip
            const content = item.isExternal ? (
                <Tooltip content={item.label} relationship="label" positioning="after">
                    <a href={item.href} target="_blank" rel="noopener noreferrer">
                        {renderNavItemContent(item)}
                    </a>
                </Tooltip>
            ) : (
                <Tooltip content={item.label} relationship="label" positioning="after">
                    <Link to={item.path!} aria-current={isCurrentPage ? "page" : undefined}>
                        {renderNavItemContent(item)}
                    </Link>
                </Tooltip>
            );

            return (
                <li key={item.id} className={itemClasses}>
                    {content}
                </li>
            );
        }

        // Expanded nav: no tooltip needed
        const content = item.isExternal ? (
            <a href={item.href} target="_blank" rel="noopener noreferrer">
                {renderNavItemContent(item)}
            </a>
        ) : (
            <Link to={item.path!} aria-current={isCurrentPage ? "page" : undefined}>
                {renderNavItemContent(item)}
            </Link>
        );

        return (
            <li key={item.id} className={itemClasses}>
                {content}
            </li>
        );
    };

    return (
        <>
            <div className={mergeClasses(styles.mobileOverlay, isMenuOpen && styles.mobileOverlayVisible)} aria-hidden="true" />

            <nav className={mergeClasses(styles.navigationBar, isMenuOpen && styles.expanded)} aria-label="Main navigation">
                <div className={styles.mobileHeader}>
                    <span className={styles.mobileTitle}>{brand.name}</span>
                    <button className={styles.closeButton} onClick={toggleNav} aria-label="Close navigation menu">
                        <XIcon size={24} aria-hidden="true" />
                    </button>
                </div>

                <button
                    className={mergeClasses(styles.menuButton, isMenuOpen && styles.menuButtonExpanded)}
                    onClick={toggleNav}
                    aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                    aria-expanded={isMenuOpen}
                >
                    <ListIcon size={28} style={{ fill: brand.colors.navMenuIcon }} aria-hidden="true" />
                    <span className={mergeClasses(styles.menuButtonText, isMenuOpen && styles.menuButtonTextVisible)} aria-hidden="true">
                        {brand.name}
                    </span>
                </button>

                <ul className={styles.navigationMenus}>{navigationItems.map(renderNavItem)}</ul>

                {/* Only show user profile section when auth is enabled */}
                {authEnabled && (
                    <div className={styles.userProfile}>
                        <div className={mergeClasses(styles.userProfileItem, isActive("/profile") && styles.userProfileItemActive)}>
                            {!isMenuOpen && !isMobile ? (
                                <Tooltip content={userName} relationship="label" positioning="after">
                                    <Link to="/profile" aria-current={isActive("/profile") ? "page" : undefined}>
                                        <span className={styles.navItemIcon}>
                                            <UserIcon size={28} aria-hidden="true" />
                                        </span>
                                        <span className={mergeClasses(styles.userProfileText, isMenuOpen && styles.userProfileTextVisible)}>{userName}</span>
                                    </Link>
                                </Tooltip>
                            ) : (
                                <Link to="/profile" aria-current={isActive("/profile") ? "page" : undefined} aria-label={`Profile: ${userName}`}>
                                    <span className={styles.navItemIcon}>
                                        <UserIcon size={28} aria-hidden="true" />
                                    </span>
                                    <span className={mergeClasses(styles.userProfileText, isMenuOpen && styles.userProfileTextVisible)}>{userName}</span>
                                </Link>
                            )}
                        </div>
                    </div>
                )}

                <div className={mergeClasses(styles.footer, isMenuOpen && styles.footerVisible)}>
                    <Tooltip content={`Built ${__BUILD_TIME__} from ${__BUILD_SHA__}`} relationship="label">
                        <span className={styles.versionText}>v{__APP_VERSION__}</span>
                    </Tooltip>
                </div>
            </nav>
        </>
    );
};

export default NavigationBar;
